import { EyeOutlined } from '@ant-design/icons';
import {
  GetFrontAreaPubblicaRichiesteParams,
  RichiestaPubblicaDto,
  useGetFrontAreaPubblicaRichieste,
} from '@framework/api-client';
import { TitleSubmissionRow } from '@framework/common-web';
import { useTranslation } from '@framework/i18n';
import {
  AdvancedSearch,
  BrandedPath,
  FilterButton,
  FrontFilterPanel,
  FrontMessageFilteredList,
  LinkButton,
  ResponsiveCard,
  TableActionsCellSpace,
  TableData,
  generatePath,
  getPagination,
  getPaginationUrlColumns,
  handlePaginationChange,
  useBreadcrumbItem,
  useHistory,
  usePaginationUrlQueryState,
} from '@framework/ui';
import { Button, Divider } from 'antd';
import { isEqual, omit } from 'lodash';
import React, { useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { FilterDrawerButton } from '../../../ui/drawer/FilterDrawerButton';
import { PageLayout } from '../../../ui/layout/PageLayout';
import { NetworkAlertPage } from '../../errors/network-alert/NetworkAlertPage';
import { LoadingPage } from '../../loading/LoadingPage';
import { GetFrontExtendedFiltersSchema } from '../../submission/schema/GetFrontExtendedFiltersSchema';
import { useObservationSearchableFields } from './filter/useObservationSearchableFields';
import {
  observationsColumns,
  observationsColumnsMobile,
} from './table/observationsColumns';

export interface IObservationsListPageProps extends RouteComponentProps {}

export function ObservationsListPage(props: IObservationsListPageProps) {
  const history = useHistory();
  const { t } = useTranslation();
  // Pannello Filtro (Desktop)
  const [openSearchPanel, setOpenSearchPanel] = useState(false);
  // Filtro Mobile (Drawer)
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const isMobile = useBreakpoint(down('md'));

  const defaultQuery = useMemo(
    () =>
      getPagination({
        page: 0,
        pageSize: 10,
        // sort: 'dataModifica',
        // direction: ColumnSortDirections.descend,
      }),
    [],
  );

  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: t('observation.area-pubblica', 'Area Pubblica'),
  });

  const [query, setQuery] =
    usePaginationUrlQueryState<GetFrontAreaPubblicaRichiesteParams>({
      history,
      input: defaultQuery,
    });

  const { data, error, isLoading, isFetching } =
    useGetFrontAreaPubblicaRichieste(query, {
      query: { keepPreviousData: true },
    });

  const observations = data?.dati;
  const meta = data?.meta;

  const isFiltered = useMemo(() => {
    if (isFetching) return false;
    return !isEqual(
      omit(query, ['pagina', 'ordinamenti', 'limite']),
      omit(defaultQuery, ['pagina', 'ordinamenti', 'limite']),
    );
  }, [query, isFetching]);

  const defaultColumns = isMobile
    ? observationsColumnsMobile
    : observationsColumns;

  const columns = getPaginationUrlColumns<
    RichiestaPubblicaDto,
    GetFrontAreaPubblicaRichiesteParams
  >(defaultColumns, query);

  const searchableFields = useObservationSearchableFields();

  const onPaginationChange = handlePaginationChange<
    RichiestaPubblicaDto,
    GetFrontAreaPubblicaRichiesteParams
  >({
    columns,
    defaultQuery,
    query,
    setQuery,
    getFilters: filters => ({
      // statoRichiesta: filters
      //   ?.statoRichiesta?.[0] as GetFrontRichiesteStatoRichiesta,
    }),
  });

  const filterform = (
    <AdvancedSearch
      id="observation-list"
      schema={GetFrontExtendedFiltersSchema}
      fields={searchableFields}
      query={query}
      setQuery={setQuery}
      defaultQuery={defaultQuery}
      pageKey="pagina"
      defaultFieldName="titoloTipoDomanda"
    />
  );

  if (isLoading ?? isFetching) return <LoadingPage />;

  if (error) {
    return (
      <NetworkAlertPage
        message={t(
          'observation.impossibile-caricare-elenco-area-pubblica',
          "Impossibile caricare l'elenco dell'Area Pubblica",
        )}
        error={error}
      />
    );
  }

  return (
    <PageLayout
      extraButtons={
        isMobile && (
          <FilterDrawerButton
            open={openFilterDrawer}
            onOpen={() => setOpenFilterDrawer(true)}
            onClose={() => setOpenFilterDrawer(false)}
          >
            {filterform}
          </FilterDrawerButton>
        )
      }
    >
      <ResponsiveCard
        noPadding
        title={
          <TitleSubmissionRow
            right={
              !isMobile && (
                <FilterButton
                  opened={openSearchPanel}
                  id="filter-button-observations-list"
                  onClick={() => setOpenSearchPanel(!openSearchPanel)}
                >
                  {t('observation.ricerca', 'Ricerca')}
                </FilterButton>
              )
            }
          >
            {t('observation.area-pubblica', 'Area Pubblica')}
          </TitleSubmissionRow>
        }
      >
        <Divider style={{ margin: 0 }} />

        <FrontFilterPanel
          id="filter-panel-observations-list"
          buttonId="filter-button-observations-list"
          open={!isMobile && openSearchPanel}
        >
          {filterform}
        </FrontFilterPanel>

        <FrontMessageFilteredList
          visible={isFiltered}
          items={meta?.totale ?? null}
          message={t(
            'observation.domande-trovate-in-base-ai-filtri-di-ricerca',
            'domande trovate in base ai filtri di ricerca.',
          )}
          action={
            <Button
              size="small"
              onClick={() => setQuery(defaultQuery)}
              children={t('observation.rimuovi-filtri', 'Rimuovi filtri')}
            />
          }
        />

        <TableData
          showSorterTooltip={false}
          size="small"
          dataSource={observations ?? []}
          loading={isLoading || isFetching}
          pagination={{
            current: Number(query.pagina ?? 0) + 1,
            pageSize: query.limite != null ? Number(query.limite) : 10,
            total: meta?.totale ?? 0,
            pageSizeOptions: [5, 10, 20, 50],
          }}
          onChange={onPaginationChange}
          columns={[
            ...columns,
            {
              title: 'Azioni',
              key: 'actions',
              align: 'right',
              render: (text, record) => (
                <TableActionsCellSpace>
                  <LinkButton
                    aria-label={`Vai alla richiesta ${
                      record.richiesta?.progressivo ?? ''
                    }`}
                    type="link"
                    size="large"
                    icon={<EyeOutlined aria-hidden />}
                    disabled={!record.richiesta?.id}
                    to={generatePath('/area-pubblica/:id', {
                      id: record.richiesta?.id ?? '',
                    })}
                  />
                </TableActionsCellSpace>
              ),
            },
          ]}
        />
      </ResponsiveCard>
    </PageLayout>
  );
}
